import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  findCategoryById,
  resolveCategoryId,
  CURRENCIES,
  formatPhoneMask,
  normalizeTelegram,
  validatePhone,
  validateTelegram,
  validateWhatsApp,
  MAX_WANTED_LENGTH,
} from '@barter/shared';
import type { AdCondition, Currency } from '@barter/shared';
import { getAdById } from '../../db/database';
import { enqueueUpdate } from '../../db/sync-queue';
import { compressImageFile } from '../../services/images';
import { useClassifier } from '../../hooks/useClassifier';
import { PhotoUploader } from '../../components/PhotoUploader/PhotoUploader';
import { CitySelect } from '../../components/CitySelect/CitySelect';
import { CategoryPicker } from '../../components/CategoryPicker/CategoryPicker';
import { useUser } from '../../context/UserContext';

export function EditAdPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, userReady } = useUser();
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const { result: classResult, loading: classLoading, classify } = useClassifier();

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [negotiable, setNegotiable] = useState(false);
  const [currency, setCurrency] = useState<Currency>('UAH');
  const [categoryId, setCategoryId] = useState('');
  const [categoryManual, setCategoryManual] = useState(true);
  const [condition, setCondition] = useState<AdCondition | ''>('');
  const [wanted, setWanted] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [telegram, setTelegram] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [preferred, setPreferred] = useState<'telegram' | 'phone' | 'whatsapp'>('telegram');
  const [previews, setPreviews] = useState<string[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id || !userReady) return;
    setLoading(true);

    (async () => {
      try {
        const ad = await getAdById(id);

        if (!ad) {
          setError(tRef.current('common.error'));
          setLoading(false);
          return;
        }

        if (user && ad.sellerTelegramId !== user.id) {
          setError(tRef.current('common.error'));
          setLoading(false);
          return;
        }

        setTitle(ad.title);
        setDescription(ad.description);
        setPrice(ad.price !== null ? String(ad.price) : '');
        setNegotiable(ad.price === null);
        setCurrency(ad.currency as Currency);
        setCategoryId(resolveCategoryId(ad.categoryId));
        setCondition(ad.condition ?? '');
        setWanted(ad.wanted ?? '');
        setMake(ad.make ?? '');
        setModel(ad.model ?? '');
        setCity(ad.location?.city || '');
        setPhone(ad.contacts.phone ? formatPhoneMask(ad.contacts.phone) : '');
        setTelegram(ad.contacts.telegram ? normalizeTelegram(ad.contacts.telegram) : '');
        setWhatsapp(ad.contacts.whatsapp ? formatPhoneMask(ad.contacts.whatsapp) : '');
        setPreferred(ad.contacts.preferred === 'email' ? 'telegram' : ad.contacts.preferred);
        setExistingPhotos(ad.photos ?? []);
        setPreviews(ad.photos ?? []);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user, userReady]);

  useEffect(() => {
    if (title.length >= 3) {
      classify(title, description);
    }
  }, [title, description, classify]);

  useEffect(() => {
    if (classResult && !categoryManual) {
      setCategoryId(classResult.categoryId);
    }
  }, [classResult, categoryManual]);

  const handleCategoryChange = (val: string) => {
    setCategoryId(val);
    setCategoryManual(true);
  };

  const handlePhotosAdd = useCallback((files: File[]) => {
    const newPreviews = files.map((f) => URL.createObjectURL(f));
    setPreviews((prev) => [...prev, ...newPreviews]);
    setPendingFiles((prev) => [...prev, ...files]);
  }, []);

  const handlePhotoRemove = useCallback((index: number) => {
    setPreviews((prev) => {
      const url = prev[index];
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
        setPendingFiles((pf) => {
          const blobIdx = prev.slice(0, index).filter((u) => u.startsWith('blob:')).length;
          return pf.filter((_, i) => i !== blobIdx);
        });
      } else if (url) {
        setExistingPhotos((ep) => ep.filter((p) => p !== url));
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleSubmit = async () => {
    setError('');

    if (title.trim().length < 3) {
      setError(t('editAd.name'));
      return;
    }
    if (description.trim().length < 10) {
      setError(t('editAd.description'));
      return;
    }
    if (!categoryId) {
      setError(t('createAd.categoryRequired'));
      return;
    }
    const phoneErr = validatePhone(phone);
    if (phoneErr) {
      setError(t(phoneErr));
      return;
    }
    const tgErr = validateTelegram(telegram);
    if (tgErr) {
      setError(t(tgErr));
      return;
    }
    const wsErr = validateWhatsApp(whatsapp);
    if (wsErr) {
      setError(t(wsErr));
      return;
    }

    setSubmitting(true);

    try {
      // Новые фото сжимаем на клиенте; уже опубликованные ключи photos/... как были
      const newDataUrls = await Promise.all(pendingFiles.map((f) => compressImageFile(f)));
      const allPhotos = [...existingPhotos, ...newDataUrls];

      const updateData = {
        title: title.trim(),
        description: description.trim(),
        price: negotiable ? null : price ? Number(price) : null,
        currency,
        categoryId,
        condition: condition || null,
        wanted: wanted.trim() || null,
        make: make.trim() || null,
        model: model.trim() || null,
        photos: allPhotos,
        contacts: {
          phone: phone || undefined,
          telegram: telegram || undefined,
          whatsapp: whatsapp || undefined,
          preferred,
        },
        location: city ? { city } : null,
      };

      await enqueueUpdate(id!, updateData);
      navigate(`/ad/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-state">{t('common.loading')}</div>;
  if (error && !title) return <div className="error-state">{error}</div>;

  return (
    <div className="create-ad">
      <h2 className="section-title">{t('editAd.title')}</h2>

      {error && <div className="error-state">{error}</div>}

      <div className="form-group">
        <label className="form-label">{t('editAd.name')}</label>
        <input
          className="form-input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('editAd.namePlaceholder')}
          maxLength={200}
        />
      </div>

      <div className="form-group">
        <label className="form-label">{t('editAd.description')}</label>
        <textarea
          className="form-textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('editAd.descriptionPlaceholder')}
          maxLength={5000}
          rows={4}
        />
      </div>

      <div className="form-group">
        <label className="form-label">{t('editAd.category')}</label>
        {classResult && !categoryManual && (
          <div className="ai-hint">
            {t('editAd.aiHint', {
              icon: findCategoryById(classResult.categoryId)?.icon,
              name: findCategoryById(classResult.categoryId)
                ? t(
                    findCategoryById(classResult.categoryId)!.nameKey ??
                      findCategoryById(classResult.categoryId)!.id,
                  )
                : '',
            })}
            {classLoading && ' ...'}
          </div>
        )}
        <CategoryPicker value={categoryId} onChange={handleCategoryChange} />
      </div>

      <div className="form-group">
        <label className="form-label">{t('editAd.condition')}</label>
        <div className="condition-toggle">
          <button
            type="button"
            className={`condition-toggle__btn${condition === 'new' ? ' condition-toggle__btn--active-new' : ''}`}
            onClick={() => setCondition(condition === 'new' ? '' : 'new')}
          >
            ✨ {t('editAd.conditionNew')}
          </button>
          <button
            type="button"
            className={`condition-toggle__btn${condition === 'used' ? ' condition-toggle__btn--active-used' : ''}`}
            onClick={() => setCondition(condition === 'used' ? '' : 'used')}
          >
            ♻️ {t('editAd.conditionUsed')}
          </button>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('editAd.wanted')}</label>
        <input
          className="form-input"
          type="text"
          value={wanted}
          onChange={(e) => setWanted(e.target.value)}
          placeholder={t('editAd.wantedPlaceholder')}
          maxLength={MAX_WANTED_LENGTH}
        />
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.car')}</label>
        <div className="form-row">
          <input
            className="form-input"
            type="text"
            value={make}
            onChange={(e) => setMake(e.target.value)}
            placeholder={t('createAd.makePlaceholder')}
            maxLength={40}
          />
          <input
            className="form-input"
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder={t('createAd.modelPlaceholder')}
            maxLength={60}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('editAd.price')}</label>
        <div className="form-row">
          <input
            className="form-input"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0"
            disabled={negotiable}
          />
          <select
            className="form-select form-select--small"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <label className="form-checkbox">
          <input
            type="checkbox"
            checked={negotiable}
            onChange={(e) => setNegotiable(e.target.checked)}
          />
          {t('editAd.negotiable')}
        </label>
      </div>

      <div className="form-group">
        <label className="form-label">{t('editAd.city')}</label>
        <CitySelect value={city} onChange={setCity} placeholder={t('editAd.selectCity')} />
      </div>

      <div className="form-group">
        <label className="form-label">{t('editAd.photos')}</label>
        <PhotoUploader previews={previews} onAdd={handlePhotosAdd} onRemove={handlePhotoRemove} />
      </div>

      <div className="form-group">
        <label className="form-label">{t('editAd.contacts')}</label>
        <input
          className="form-input"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(formatPhoneMask(e.target.value))}
          placeholder={t('editAd.phonePlaceholder')}
        />
        <input
          className="form-input"
          type="text"
          value={telegram}
          onChange={(e) => setTelegram(normalizeTelegram(e.target.value))}
          placeholder={t('editAd.telegramPlaceholder')}
        />
        <input
          className="form-input"
          type="tel"
          value={whatsapp}
          onChange={(e) => setWhatsapp(formatPhoneMask(e.target.value))}
          placeholder={t('editAd.whatsappPlaceholder')}
        />
        <select
          className="form-select"
          value={preferred}
          onChange={(e) => setPreferred(e.target.value as typeof preferred)}
        >
          <option value="telegram">{t('editAd.preferredTelegram')}</option>
          <option value="phone">{t('editAd.preferredPhone')}</option>
          <option value="whatsapp">{t('editAd.preferredWhatsapp')}</option>
        </select>
      </div>

      <button className="btn btn--primary btn--full" onClick={handleSubmit} disabled={submitting}>
        {submitting ? t('editAd.saving') : t('editAd.save')}
      </button>
    </div>
  );
}
